import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { TrendIndicator } from './TrendIndicator'
import type { TrendDirection } from '@/types'

interface HeartRateData {
  date: string
  value: number
  min?: number
  max?: number
}

interface HeartRateChartProps {
  data: HeartRateData[]
  restingHeartRate?: number
  trend?: {
    direction: TrendDirection
    changePercent?: number
  }
  isLoading?: boolean
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatShortDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function ChartSkeleton() {
  return (
    <div className="hearth-card p-5">
      <div className="h-5 w-28 bg-gray-200 rounded animate-pulse" />
      <div className="mt-4 h-64 bg-gray-100 rounded animate-pulse" />
    </div>
  )
}

export function HeartRateChart({
  data,
  restingHeartRate,
  trend,
  isLoading,
}: HeartRateChartProps) {
  if (isLoading) {
    return <ChartSkeleton />
  }

  const average = data.length > 0
    ? Math.round(data.reduce((sum, d) => sum + d.value, 0) / data.length)
    : 0

  return (
    <div className="hearth-card p-5">
      <div className="mb-4 flex flex-row items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">Heart rate</h3>
          <p className="mt-0.5 text-[13px] text-gray-500">
            Average <span className="font-rounded font-medium tabular-nums">{average}</span> bpm
            {restingHeartRate ? ` · Resting ${restingHeartRate} bpm` : ''}
          </p>
        </div>
        {trend && (
          <TrendIndicator
            trend={trend.direction}
            changePercent={trend.changePercent}
            showLabel
            size="sm"
          />
        )}
      </div>
      <div>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="heartRateGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C9634A" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#C9634A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-gray-200)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatShortDate}
                tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                axisLine={{ stroke: 'var(--color-gray-200)' }}
                tickLine={false}
              />
              <YAxis
                domain={['dataMin - 10', 'dataMax + 10']}
                tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `${value}`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload as HeartRateData
                    return (
                      <div className="rounded-lg border border-gray-200 bg-surface px-3 py-2 shadow-lg">
                        <p className="text-xs text-gray-500">{formatDate(data.date)}</p>
                        <p className="font-rounded text-sm font-semibold tabular-nums text-gray-900">
                          {data.value} bpm
                        </p>
                        {data.min !== undefined && data.max !== undefined && (
                          <p className="text-xs text-gray-500">
                            Range: {data.min} - {data.max} bpm
                          </p>
                        )}
                      </div>
                    )
                  }
                  return null
                }}
              />
              {restingHeartRate && (
                <ReferenceLine
                  y={restingHeartRate}
                  stroke="var(--color-gray-400)"
                  strokeDasharray="5 5"
                  label={{
                    value: 'Resting',
                    position: 'right',
                    fill: 'var(--color-gray-400)',
                    fontSize: 12,
                  }}
                />
              )}
              <Area
                type="monotone"
                dataKey="value"
                stroke="#C9634A"
                strokeWidth={2}
                fill="url(#heartRateGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-64 items-center justify-center">
            <p className="text-sm text-gray-500">Heart rate readings appear here as they sync.</p>
          </div>
        )}
      </div>
    </div>
  )
}
